import React, { useEffect, useState } from 'react';

const STEPS = [
  { label: 'Generating name candidates', duration: 500 },
  { label: 'Checking domain availability', duration: 700 },
  { label: 'Scanning USPTO trademarks', duration: 600 },
  { label: 'Scoring brand viability', duration: 500 },
];

type StepState = 'pending' | 'active' | 'done';

interface LoadingOverlayProps {
  visible: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ visible }) => {
  const [stepStates, setStepStates] = useState<StepState[]>(
    STEPS.map(() => 'pending'),
  );

  useEffect(() => {
    if (!visible) {
      setStepStates(STEPS.map(() => 'pending'));
      return;
    }

    let cancelled = false;
    let elapsed = 0;

    STEPS.forEach((step, i) => {
      const activateAt = elapsed;
      const doneAt = elapsed + step.duration;
      elapsed += step.duration;

      setTimeout(() => {
        if (cancelled) return;
        setStepStates((prev) => {
          const next = [...prev] as StepState[];
          next[i] = 'active';
          return next;
        });
      }, activateAt);

      setTimeout(() => {
        if (cancelled) return;
        setStepStates((prev) => {
          const next = [...prev] as StepState[];
          next[i] = 'done';
          return next;
        });
      }, doneAt);
    });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  return (
    <div
      className={`absolute inset-0 z-20 flex items-center justify-center bg-[#09090f]/90 backdrop-blur-sm transition-opacity duration-300 ${
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="bg-[#111120] rounded-2xl border border-[#1c1c2e] shadow-2xl px-8 py-7 w-72">
        <p className="text-sm font-semibold text-gray-200 mb-5">Analyzing your idea…</p>
        <ul className="space-y-3.5">
          {STEPS.map((step, i) => {
            const state = stepStates[i];
            return (
              <li key={step.label} className="flex items-center gap-3">
                <StepDot state={state} />
                <span
                  className={`text-sm transition-colors ${
                    state === 'active'
                      ? 'font-semibold text-indigo-300'
                      : state === 'done'
                      ? 'text-gray-600 line-through'
                      : 'text-gray-600'
                  }`}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

function StepDot({ state }: { state: StepState }) {
  if (state === 'done') {
    return (
      <span className="w-4 h-4 flex-shrink-0 rounded-full bg-green-500 flex items-center justify-center">
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className="w-4 h-4 flex-shrink-0 rounded-full bg-indigo-600 animate-pulse" />
    );
  }
  return (
    <span className="w-4 h-4 flex-shrink-0 rounded-full bg-gray-200" />
  );
}
